import type { FC, MouseEvent as ReactMouseEvent } from 'react';

import classNames from 'classnames';

import { Icon } from '@iconik-io/uad';

import type { TagValueComponentProps } from '../../../ui/select_field/types';
import Ellipsis from '../../ellipsis/ellipsis';

import styles from './select_field_tag_cloud_value.module.css';

const THRESHOLD = 0.7;

const SelectFieldTagCloudValue: FC<TagValueComponentProps> = (props) => {
  const { value, isDisabled, onChange, onDelete, onCopySelectionToggle } =
    props;
  const hideSideButtons = isDisabled || value.isCopyMode;

  const handleDeleteClick = (): void => {
    if (isDisabled) {
      return;
    }

    onDelete?.(value);
  };

  const handleChange = (): void => {
    if (isDisabled) {
      return;
    }
    const changedValue = {
      ...value,
      score: !value.score || value.score < THRESHOLD ? 1 : 0,
    };
    onChange?.(changedValue);
  };

  const handleMouseDown = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (value.isCopyMode) {
      event.preventDefault();
    }
  };

  return (
    <div
      className={classNames(styles.root, {
        [styles.disabled]: isDisabled,
        [styles.copy_mode]: value.isCopyMode,
        [styles.selected]: value.isCopyMode && value.isSelectedToCopy,
      })}
      onMouseDown={handleMouseDown}
      onClick={() => {
        if (value.isCopyMode) {
          onCopySelectionToggle?.();
        }
      }}
      role="listitem"
    >
      <button
        type="button"
        className={classNames(styles.delete, {
          [styles.hidden]: hideSideButtons,
        })}
        disabled={hideSideButtons}
        onClick={hideSideButtons ? undefined : handleDeleteClick}
        aria-label={`Remove ${value.label}`}
      >
        <Icon name="deletion_erase" />
      </button>
      <div
        className={styles.label}
        onClick={hideSideButtons ? undefined : handleChange}
      >
        <Ellipsis
          componentProps={{ className: styles.wrap_text }}
          tooltipText={isDisabled ? value.label : undefined}
          component="span"
          tooltipPlacement="left"
        >
          {value.label}
        </Ellipsis>
      </div>
      <div
        className={classNames(styles.inaccurate, {
          [styles.hidden]:
            hideSideButtons ||
            !(value.score !== undefined && value.score < THRESHOLD),
        })}
      >
        ?
      </div>
    </div>
  );
};

export default SelectFieldTagCloudValue;
